import { useEffect, useState } from 'react';
import { Button, Checkbox, Flex, Form, Input } from 'antd';
import type { FormProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useTimer } from 'react-timer-hook';
import { motion, AnimatePresence } from 'framer-motion';

// services
import { onLogin, onVerifyOtp, VerifyOtpPayload } from 'modules/auth/services/auth';
import authService from 'core/services/auth';
import NotificationService from 'core/services/notification';
import { getDefaultLanguage, onChangeLanguage } from 'core/services/language';

//models
import { Language } from 'core/models/common';

// styles
import logo from 'assets/images/logo.svg';
import './login.scss';

function Login() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [remember, setRemember] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [step, setStep] = useState<'login' | 'otp'>('login');
	const [otpTtl, setOtpTtl] = useState<number>(120);
	const [phoneNumber, setPhoneNumber] = useState<{ countryCode: string; number: string }>();
	const [isOTPInvalid, setIsOTPInvalid] = useState(false);
	const [currentLanguage, setCurrentLanguage] = useState<Language>();
	const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

	const { start, minutes, seconds } = useTimer({
		expiryTimestamp: dayjs().add(otpTtl, 'second').toDate(),
		autoStart: false,
	});

	type LoginFieldType = {
		email: string;
		password: string;
	};

	type otpFieldType = {
		code: string;
	};

	const changeLanguage = () => {
		onChangeLanguage();
		setCurrentLanguage(currentLanguage === 'ar' ? 'en' : 'ar');
	};

	useEffect(() => {
		setCurrentLanguage(getDefaultLanguage());
	}, []);

	const onLoginFinish: FormProps<LoginFieldType>['onFinish'] = async (values) => {
		try {
			setIsLoading(true);

			const response = await onLogin(values.email, values.password);

			if (response.data.success) {
				setOtpTtl(response.data?.data?.ttl || 120);

				setPhoneNumber({
					countryCode: response.data?.data?.phone_country_code,
					number: response.data?.data?.phone_number,
				});

				start();

				setIsLoading(false);

				setStep('otp');

				setTimeoutId(
					setTimeout(() => {
						setStep('login');
						setIsOTPInvalid(false);
					}, otpTtl * 1000),
				);
			} else {
				setIsLoading(false);
				NotificationService.error('Ops !!', 'An error occurred. Please contact our support.');
			}
		} catch (error: any) {
			setIsLoading(false);
			if (error.status === 400) NotificationService.error('Ops !!', 'Admin information is not completed');
			if (error.status === 401) NotificationService.error('Ops !!', 'These credentials do not match our records.');
			if (error.status === 403)
				NotificationService.error('Ops !!', 'Your account has been marked as inactive. Please contact the administrator to reactivate it.');
		}
	};

	const onOtpFinish: FormProps<otpFieldType>['onFinish'] = async (values) => {
		try {
			setIsLoading(true);

			const payload: VerifyOtpPayload = {
				phone_country_code: phoneNumber?.countryCode!,
				phone_number: phoneNumber?.number!,
				code: values.code,
				device_token: 'xyz',
			};

			const response = await onVerifyOtp(payload);

			authService.setToken(response.data.data.authorization.split('Bearer ')[1], remember);
			authService.setUser(response.data.data);
			authService.setPermissions(response.data.data.permissions);
			authService.setRoles(response.data.data.roles);

			clearTimeout(timeoutId!);

			navigate('/');
		} catch (error) {
			setIsLoading(false);
			setIsOTPInvalid(true);
			NotificationService.error('Ops !!', 'Invalid OTP. Please try again.');
		}
	};

	return (
		<Flex justify="center" align="center" gap={40} vertical style={{ width: '100vw', height: '100vh', background: '#F1F1F2' }}>
			<img src={logo} alt="logo" className="logo" />

			<AnimatePresence mode="wait">
				{step == 'login' ? (
					<motion.div
						className="login"
						initial={{ opacity: 0, transform: 'translateY(20px)' }}
						animate={{ opacity: 1, transform: 'translateY(0px)' }}
						exit={{ opacity: 0, transform: 'translateY(-20px)' }}
						transition={{ duration: 0.5 }}
						key="login">
						<div>
							<h1 className="login_title">{t('LOGIN.TITLE')} 👋</h1>
							<h4 className="login_subTitle">{t('LOGIN.SUBTITLE')}</h4>

							<Form name="login" onFinish={onLoginFinish}>
								<Form.Item name="email" rules={[{ required: true, message: t('COMMON.EMAIL_REQUIRED') }]} style={{ maxWidth: 'unset' }}>
									<Input placeholder={t('COMMON.EMAIL')} type="email" size="large" />
								</Form.Item>

								<Form.Item
									name="password"
									rules={[{ required: true, message: t('COMMON.PASSWORD_REQUIRED') }]}
									style={{ maxWidth: 'unset' }}>
									<Input.Password size="large" type="password" placeholder={t('COMMON.PASSWORD')} />
								</Form.Item>

								<Checkbox style={{ marginBlock: '10px 20px' }} checked={remember} onChange={(e) => setRemember(e.target.checked)}>
									{t('LOGIN.REMEMBER_ME')}
								</Checkbox>

								<Form.Item style={{ maxWidth: 'unset' }}>
									<Button block size="large" type="primary" htmlType="submit" loading={isLoading}>
										{t('LOGIN.LOGIN')}
									</Button>
								</Form.Item>
							</Form>

							<Button type="text" style={{ display: 'block', margin: '20px auto 0 auto' }} onClick={changeLanguage}>
								{currentLanguage === 'ar' ? 'English' : 'العربية'}
							</Button>
						</div>
					</motion.div>
				) : (
					<motion.div
						className="otp"
						initial={{ opacity: 0, transform: 'translateY(20px)' }}
						animate={{ opacity: 1, transform: 'translateY(0px)' }}
						exit={{ opacity: 0, transform: 'translateY(-20px)' }}
						transition={{ duration: 0.5 }}
						key="otp">
						<div>
							<h3 className="login_title">{t('LOGIN.OTP_VERIFICATION')}</h3>
							<h6 className="login_subTitle mb-4">{t('LOGIN.ENTER_OTP')}</h6>

							<Form name="otpForm" onFinish={onOtpFinish}>
								<Form.Item name="code" style={{ maxWidth: 'unset' }} className="mb-4" rules={[{ required: true, min: 4, max: 4 }]}>
									<Input.OTP length={4} style={{ direction: 'ltr' }} type="number" status={isOTPInvalid ? 'error' : ''} />
								</Form.Item>

								{isOTPInvalid && (
									<p style={{ fontSize: '16px', fontWeight: 500, color: 'red', textAlign: 'center' }}>{t('LOGIN.INVALID_OTP')}</p>
								)}

								<div className="timer">
									<p>{minutes}</p>
									<p>:</p>
									<p>{seconds}</p>
								</div>

								<Button block type="primary" htmlType="submit" loading={isLoading}>
									{t('COMMON.VERIFY')}
								</Button>
							</Form>

							<Button type="text" style={{ display: 'block', margin: '20px auto 0 auto' }} onClick={changeLanguage}>
								{currentLanguage === 'ar' ? 'English' : 'العربية'}
							</Button>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</Flex>
	);
}

export default Login;
